import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {useMemo} from 'react'
import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import type {AffiliateLink, BuyingOption, RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {ArrowIcon} from '~/icons/product-page/arrow-icon'
import DiscountCornerRibbon from '../badges/DiscountCornerRibbon'
import BestValue from '../badges/BestValue'

interface BuyingOptionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number
  buyingOption: BuyingOption
  productName: string
  type: 'main-cta' | 'options-cta'
  handleCardClick: (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkWithClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => void
  shouldForceTrackAnalytics: boolean
  trackSearchParams: string
  clickId: string
}
const BuyingOptionCard: FunctionComponent<BuyingOptionCardProps> = ({
  buyingOption,
  index,
  productName,
  type,
  handleCardClick,
  shouldForceTrackAnalytics,
  trackSearchParams,
  clickId,
}: BuyingOptionCardProps) => {
  const {language, currencyCode, locale, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles} = getDomainConfig(domain, locale)
  const {partner, discount, price, affiliateLink, freeShipping, storeLogoUrl, shippingCost, isBestValue} = buyingOption

  const [cardRef] = useReportWidgetSeen({
    widget_name: 'css-buying-option',
    widget_place: type === 'main-cta' ? type : `${type}-${partner}-${index + 1}`,
  })

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [language, currencyCode],
  )

  const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  return (
    <div ref={cardRef} className={clsx('relative px-[1px]', type === 'main-cta' && 'tablet:mt-3 tablet:py-2')}>
      <a
        href={redirectUrl}
        onClick={(e) =>
          handleCardClick(
            e,
            index + 1,
            affiliateLink,
            affiliateLinkWithClickId,
            productName,
            redirectUrl,
            'css-buying-option',
            type === 'main-cta' ? type : `${type}-${partner}-${index + 1}`,
          )
        }
        aria-label={`view ${productName}`}
        target="_blank"
        rel="sponsored noreferrer"
        className={clsx(
          'flex min-h-[85px] w-full flex-row items-center justify-between gap-4 rounded-xl border bg-white p-2 tablet:gap-2',
          index === 0 ? styles.CARD_BORDER : 'relative border-[#C6CDD7]',
          type === 'main-cta' ? 'tablet:flex-col-reverse' : 'tablet:min-h-[70px] tablet:flex-col tablet:justify-center',
          type === 'options-cta' && isBestValue && 'shadow-[0px_4px_7.9px_0px_#00000026]',
        )}
      >
        {isBestValue ? (
          <BestValue
            className={clsx(
              'absolute -top-3 !z-20 tablet:left-1/2 tablet:-translate-x-1/2',
              type === 'main-cta' ? 'left-1/2 -translate-x-1/2 tablet:-top-1' : '-top-3 left-24',
            )}
          />
        ) : null}

        {discount && discount > 0 ? (
          <DiscountCornerRibbon
            className={clsx(
              'absolute -top-[1px] left-0 tablet:-left-[1px] tablet:-top-[2px] [&>div>.ribbon]:size-14',
              type === 'main-cta' && 'tablet:hidden',
            )}
            discount={discount}
          />
        ) : null}

        <div
          className={clsx(
            'flex flex-row justify-between gap-5 desktop:w-[70%] tablet:w-full tablet:justify-between tablet:gap-2',
            type === 'options-cta' && 'desktop:flex-1',
          )}
        >
          <div
            className={clsx(
              'ml-5 flex items-center justify-center gap-1 text-xs text-gray-500 tablet:ml-2',
              type === 'main-cta' ? 'flex-col' : 'desktop:flex-row desktop:gap-3 tablet:flex-col',
            )}
          >
            <span className={clsx(type === 'options-cta' && 'after:content-[":"]')}>
              {translations.ProductPage.availableOn}
            </span>
            <div className="h-8 w-20">
              <img src={storeLogoUrl} alt={partner} className="h-8 w-20 object-contain" loading="eager" />
            </div>
          </div>
          <div className="flex flex-1 flex-row items-center justify-end *:min-h-10">
            {freeShipping || (shippingCost && !freeShipping) ? (
              <div className="flex h-fit items-center justify-center pr-3 text-center text-xs font-light text-gray-500 tablet:pr-1.5 tablet:text-[11px]">
                {translations.ProductPage.shippingMethod(
                  freeShipping,
                  shippingCost && !freeShipping ? currencyFormatter.format(shippingCost) : undefined,
                )}
              </div>
            ) : null}
            <div
              className={clsx(
                'flex h-fit items-center justify-center gap-1 border-l border-[#64748B]/30 pl-3 tablet:pl-1.5',
                type === 'main-cta' ? 'flex-col' : 'desktop:flex-row desktop:gap-3 tablet:flex-col',
              )}
            >
              {price ? (
                <div className="h-fit text-[23px] font-medium leading-none text-gray-900">
                  {currencyFormatter.format(price)}
                </div>
              ) : null}
              <div
                className={clsx(
                  'h-3 text-xs leading-none text-red-600 desktop:min-w-20',
                  !discount && 'tablet:hidden',
                  !discount && type === 'main-cta' && 'hidden',
                  type === 'main-cta' && 'desktop:text-center tablet:font-semibold',
                )}
              >
                {discount && discount > 0 ? translations.ProductPage.discountCard(discount) : null}
              </div>
            </div>
            <div
              className={clsx(
                'ml-1.5 size-11 shrink-0 items-center justify-center rounded-full',
                styles.LISTPAGE_BUTTON_PRIMARY,
                type === 'options-cta' ? 'hidden tablet:flex' : 'hidden',
              )}
            >
              <ArrowIcon className="shrink-0" />
            </div>
          </div>
        </div>
        <div
          className={clsx(
            'my-auto flex h-11 min-w-[173px] items-center justify-center gap-2 rounded-full px-2 text-base font-normal tablet:w-full tablet:min-w-full',
            index === 0 ? styles.LISTPAGE_BUTTON_PRIMARY : 'bg-[#64748B] text-white',
            type === 'main-cta' ? 'tablet:mt-3' : 'tablet:hidden',
            'listpage_button_primary',
          )}
        >
          {translations.ProductPage.viewProduct}
          <ArrowIcon className="hidden shrink-0 tablet:block" />
        </div>
      </a>
    </div>
  )
}

export default BuyingOptionCard
